import { Observable, Subscription } from 'rxjs';
import { ApiResponse } from '../types';
import type {
  FamilyStartPreview,
  FamilyStartReason,
  FamilyStartResult,
  FamilyStartSubject,
} from '../types/family-start.types';
import type { FamilyMembersFlowController } from './family-members-flow.controller';
import { startFamilyReasonFromError } from './family-start.util';

/** The calls a Start Family flow makes; implemented by FamilyStartService. */
export interface StartFamilyActions {
  preview(subscriptionId: string): Observable<ApiResponse<FamilyStartPreview>>;
  start(subscriptionId: string): Observable<ApiResponse<FamilyStartResult>>;
}

export interface StartFamilyFlowOptions {
  /**
   * The add-members pop-up that follows: opened for the new family as soon as it starts, so
   * members can be added straight away (or skipped).
   */
  members?: FamilyMembersFlowController;
  /** Host follow-up after a family started: reload a list, refresh a lane event. */
  onDone?: (result: FamilyStartResult, subject: FamilyStartSubject) => void;
  /** The card is out of date (see STALE_CODES): reload so the host shows it as it is now. */
  onStale?: () => void;
}

/** Refusals that mean the card is out of date, so the host should reload it. */
const STALE_CODES = new Set([
  'FAMILY_MEMBERSHIP_NOT_ACTIVE',
  'ALREADY_IN_FAMILY',
  'SUBSCRIPTION_NOT_FOUND',
]);

/**
 * The state behind the Start Family pop-up, shared by every host so none repeats the open / load /
 * submit / error wiring. Opening loads the preview; the pop-up shows `reason` when the membership
 * cannot start a family. One request runs at a time. Plain fields, so it works with default
 * (Eager) change detection. Call `destroy()` from the host's `ngOnDestroy`.
 */
export class StartFamilyFlowController {
  target: FamilyStartSubject | null = null;
  preview: FamilyStartPreview | null = null;
  loading = false;
  busy = false;
  /** Why the membership cannot start a family, or why the last start failed. */
  reason: FamilyStartReason | null = null;
  result: FamilyStartResult | null = null;
  /** The membership the last start ran for, so the result can name it after the pop-up closed. */
  lastSubject: FamilyStartSubject | null = null;

  private inFlight: Subscription | null = null;

  constructor(
    private readonly actions: StartFamilyActions,
    private readonly options: StartFamilyFlowOptions = {}
  ) {}

  open(subject: FamilyStartSubject): void {
    if (this.busy) {
      return;
    }
    this.inFlight?.unsubscribe();
    this.target = subject;
    this.preview = null;
    this.reason = null;
    this.result = null;
    this.loading = true;
    this.inFlight = this.actions.preview(subject.subscriptionId).subscribe({
      next: response => {
        this.loading = false;
        this.preview = response.data ?? null;
        this.reason = this.preview?.reason ?? null;
      },
      error: error => {
        this.loading = false;
        this.reason = startFamilyReasonFromError(error);
      },
    });
  }

  close(): void {
    if (this.busy) {
      return;
    }
    this.inFlight?.unsubscribe();
    this.inFlight = null;
    this.target = null;
    this.preview = null;
    this.reason = null;
    this.loading = false;
  }

  /** Starts the family: no discount for the primary, nothing charged. */
  confirm(): void {
    const subject = this.target;
    if (!subject || this.busy || !this.preview?.eligible) {
      return;
    }
    this.busy = true;
    this.reason = null;
    this.inFlight = this.actions.start(subject.subscriptionId).subscribe({
      next: response => {
        this.busy = false;
        this.inFlight = null;
        this.target = null;
        this.preview = null;
        if (response.data) {
          this.result = response.data;
          this.lastSubject = subject;
          this.options.members?.open(response.data.group_id, subject, true);
          this.options.onDone?.(response.data, subject);
        }
      },
      error: error => {
        this.busy = false;
        this.inFlight = null;
        this.reason = startFamilyReasonFromError(error);
        if (STALE_CODES.has(this.reason.code)) {
          this.options.onStale?.();
        }
      },
    });
  }

  closeResult(): void {
    this.result = null;
  }

  destroy(): void {
    this.inFlight?.unsubscribe();
    this.inFlight = null;
  }
}
